import { getCurrentIdToken } from './auth';

const BASE_URL = import.meta.env.VITE_API_BASE_URL.replace(/\/$/, '');

export class ApiError extends Error {
  constructor(
    public status: number,
    public body: unknown,
  ) {
    super(
      typeof body === 'object' && body && 'error' in body
        ? String((body as { error: unknown }).error)
        : `HTTP ${status}`,
    );
  }
}

async function request<T>(path: string, init: RequestInit & { auth?: boolean } = {}): Promise<T> {
  const { auth = true, headers, ...rest } = init;
  const finalHeaders = new Headers(headers);
  if (rest.body && !finalHeaders.has('Content-Type')) {
    finalHeaders.set('Content-Type', 'application/json');
  }
  if (auth) {
    const token = await getCurrentIdToken();
    if (!token) throw new ApiError(401, { error: 'not authenticated' });
    finalHeaders.set('Authorization', `Bearer ${token}`);
  }

  let res: Response;
  try {
    res = await fetch(`${BASE_URL}${path}`, { ...rest, headers: finalHeaders });
  } catch {
    throw new ApiError(0, { error: 'Netzwerkfehler -- keine Verbindung' });
  }

  const contentType = res.headers.get('Content-Type') ?? '';
  const body = contentType.includes('application/json')
    ? await res.json().catch(() => undefined)
    : await res.text();
  if (!res.ok) throw new ApiError(res.status, body);
  return body as T;
}

export const api = {
  get: <T>(path: string, auth = true) => request<T>(path, { method: 'GET', auth }),
  post: <T>(path: string, body?: unknown, auth = true) =>
    request<T>(path, {
      method: 'POST',
      body: body !== undefined ? JSON.stringify(body) : undefined,
      auth,
    }),
  put: <T>(path: string, body?: unknown, auth = true) =>
    request<T>(path, {
      method: 'PUT',
      body: body !== undefined ? JSON.stringify(body) : undefined,
      auth,
    }),
  del: <T>(path: string, auth = true) => request<T>(path, { method: 'DELETE', auth }),
};

/** CSV/Datei-Downloads fetcht mit Auth und liefert eine Blob-URL, da ein <a href> keinen Authorization-Header tragen kann. */
export async function authedDownloadUrl(path: string): Promise<string> {
  const token = await getCurrentIdToken();
  if (!token) throw new ApiError(401, { error: 'not authenticated' });
  const res = await fetch(`${BASE_URL}${path}`, { headers: { Authorization: `Bearer ${token}` } });
  if (!res.ok) throw new ApiError(res.status, await res.text().catch(() => undefined));
  const blob = await res.blob();
  return URL.createObjectURL(blob);
}
